import {
  degMinSecToDegrees,
  degreesToRadians,
  formatAngle,
  formatLength,
  formatLengthAsFractionalFeetAndInches,
  LengthUnits,
  parseFeetInchValue,
  radiansToDegrees,
  vector3,
  Vector3,
} from '@fulcrum/lib';
import type { FormattingSettings } from '@trimble/types';
import { TcModelAlignment } from '@data-access/connect/tc-tcps';
import { UnreachableCaseError } from 'ts-essentials';
import {
  DEFAULT_IDENTITY_MODEL_ALIGNMENT,
  type Axis,
} from './position-settings.models';

const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_DEGREE = 60;

export function createModelAlignment(
  overrides: Partial<TcModelAlignment> = {}
): TcModelAlignment {
  return { ...DEFAULT_IDENTITY_MODEL_ALIGNMENT, ...overrides };
}

export function createZeroVector(): Vector3 {
  return vector3(0, 0, 0);
}

export function areAlignmentsEqual(
  a: TcModelAlignment,
  b: TcModelAlignment
): boolean {
  return (
    a.locationX === b.locationX &&
    a.locationY === b.locationY &&
    a.locationZ === b.locationZ &&
    a.axisX === b.axisX &&
    a.axisY === b.axisY &&
    a.axisZ === b.axisZ &&
    a.refDirectionX === b.refDirectionX &&
    a.refDirectionY === b.refDirectionY &&
    a.refDirectionZ === b.refDirectionZ &&
    a.scale === b.scale
  );
}

export function getVectorAxis(vector: Vector3, axis: Axis): number {
  switch (axis) {
    case 'x':
      return vector.x;
    case 'y':
      return vector.y;
    case 'z':
      return vector.z;
    default:
      throw new UnreachableCaseError(axis);
  }
}

export function setVectorAxis(
  current: Vector3,
  axis: Axis,
  value: number
): Vector3 {
  switch (axis) {
    case 'x':
      return vector3(value, current.y, current.z);
    case 'y':
      return vector3(current.x, value, current.z);
    case 'z':
      return vector3(current.x, current.y, value);
    default:
      throw new UnreachableCaseError(axis);
  }
}

export function formatModelPositionLength(
  valueMm: number,
  settings: FormattingSettings
): string {
  if (settings.lengthFormatting === 'fractional') {
    return formatLengthAsFractionalFeetAndInches(
      valueMm,
      settings.lengthFractions
    );
  }

  return formatLength(valueMm, settings.lengthUnit, settings.lengthDecimals);
}

export function parseModelPositionLength(
  value: string,
  settings: FormattingSettings,
  defaultValue: number
): number {
  const millimeters =
    settings.lengthFormatting === 'fractional'
      ? (parseFeetInchValue(value) ?? convertToMillimeters(value, settings))
      : convertToMillimeters(value, settings);

  return millimeters === undefined || Number.isNaN(millimeters)
    ? defaultValue
    : millimeters;
}

export function formatModelPositionAngle(
  valueRadians: number,
  settings: FormattingSettings
): string {
  const formattedAngle = formatAngle(
    radiansToDegrees(valueRadians),
    settings.angleUnit ?? 'deg',
    settings.angleDecimals
  );

  return normalizeDegreesMinutesSeconds(formattedAngle);
}

export function parseModelPositionAngle(
  value: string,
  settings: FormattingSettings,
  defaultValue: number
): number {
  const normalizedValue = value.replace(',', '.');

  switch (settings.angleUnit) {
    case 'rad': {
      const parsed = Number.parseFloat(normalizedValue);
      return Number.isNaN(parsed) ? defaultValue : parsed;
    }
    case 'deg-min-sec': {
      const parsedInDegrees = degMinSecToDegrees(normalizedValue);
      return Number.isNaN(parsedInDegrees)
        ? defaultValue
        : degreesToRadians(parsedInDegrees);
    }
    case 'deg':
    default: {
      const parsed = Number.parseFloat(normalizedValue);
      return Number.isNaN(parsed) ? defaultValue : degreesToRadians(parsed);
    }
  }
}

function convertToMillimeters(
  value: string,
  settings: FormattingSettings
): number | undefined {
  const parsedValue = Number.parseFloat(value.replace(',', '.'));
  const unit =
    settings.lengthFormatting === 'fractional' ? 'ft' : settings.lengthUnit;
  const currentUnit = LengthUnits[unit];

  return parsedValue * currentUnit.inMillimeters;
}

function normalizeDegreesMinutesSeconds(value: string): string {
  const match = value.match(/^(-?\d+)°\s+(\d+)'\s+(\d+(?:[.,]\d+)?)"$/);
  if (!match) {
    return value;
  }

  const [, degreesText, minutesText, secondsText] = match;
  const seconds = Number.parseFloat(secondsText.replace(',', '.'));
  if (seconds < SECONDS_PER_MINUTE) {
    return value;
  }

  const sign = degreesText.startsWith('-') ? '-' : '';
  let degrees = Math.abs(Number.parseInt(degreesText, 10));
  // DMS values roll over at 60 seconds and 60 minutes.
  let minutes =
    Number.parseInt(minutesText, 10) + Math.floor(seconds / SECONDS_PER_MINUTE);
  const normalizedSeconds = seconds % SECONDS_PER_MINUTE;

  degrees += Math.floor(minutes / MINUTES_PER_DEGREE);
  minutes %= MINUTES_PER_DEGREE;

  return `${sign}${degrees}° ${minutes}' ${normalizedSeconds}"`;
}
